import React from "react";
import { Card, Heading, Text, HStack, VStack, Divider } from "@astryxdesign/core";
import ScoreBadge from "./ScoreBadge";
import type { ScoreBadgeTier } from "./ScoreBadge";
import styles from "./TemplateSectionCard.module.css";

export interface TemplateSectionCardProps {
  index: number;
  name: string;
  avgScore?: number | null;
  maxScore: number;
  /** Passed through to the internal ScoreBadge. @default "fraction" */
  scoreFormat?: "fraction" | "percent";
  /** Passed through to the internal ScoreBadge as a server-authoritative override. */
  scoreTier?: ScoreBadgeTier;
  /** Caption above the section's score badge. @default "Avg. score" */
  scoreLabel?: string;
  children: React.ReactNode;
}

/**
 * Read-only section card for the script-template view page: an eyebrow +
 * section name on the left, the section's average score on the right, and
 * the question list (passed as `children`) below.
 *
 * Distinct from the editor's SectionCard, which renders a drag handle and a
 * delete control — this card has no mutation affordances, so it is not a
 * drop-in replacement for that one.
 *
 * Renders via Astryx's Card/Heading/Text/Divider, so this MUST be rendered
 * inside an Astryx <Theme> scope.
 */
const TemplateSectionCard = ({
  index,
  name,
  avgScore,
  maxScore,
  scoreFormat,
  scoreTier,
  scoreLabel = "Avg. score",
  children,
}: TemplateSectionCardProps): React.ReactElement => {
  return (
    <Card variant="default">
      <VStack gap={4}>
        <HStack justify="between" align="start" gap={3}>
          <VStack gap={1}>
            <Text type="supporting" color="secondary" className={styles.eyebrow}>
              Section {index}
            </Text>
            <Heading level={3}>{name || "Untitled section"}</Heading>
          </VStack>
          <VStack gap={1} align="end">
            <Text type="supporting" color="secondary">
              {scoreLabel}
            </Text>
            <ScoreBadge
              score={avgScore ?? null}
              max={maxScore}
              format={scoreFormat}
              tier={scoreTier}
            />
          </VStack>
        </HStack>
        <Divider />
        <div className={styles.body}>{children}</div>
      </VStack>
    </Card>
  );
};

export default TemplateSectionCard;
